import React, { useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import config from "../../../../../config";
import { fetchClusterInfo } from "layouts/cluster/data/fetchClusterInfo";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

const MapSelectReadOnly = ({ selectedPoint }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center,
        zoom,
        accessToken: token,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl());

      const currentGeolocation = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showUserHeading: false,
        showAccuracyCircle: false,
      });

      mapRef.current.addControl(currentGeolocation);

      currentGeolocation.on("geolocate", (e) => {
        const { longitude, latitude } = e.coords;
        mapRef.current.flyTo({
          center: [longitude, latitude],
          zoom: 15,
          essential: true,
        });
      });

      mapRef.current.on("load", async () => {
        const clusterData = await fetchClusterInfo();

        mapRef.current.getCanvas().style.cursor = "pointer";

        mapRef.current.addSource("clusters", {
          type: "geojson",
          data: clusterData,
        });

        mapRef.current.addLayer({
          id: "clusters-layer",
          type: "fill",
          source: "clusters",
          paint: {
            "fill-color": "#ff6600",
            "fill-opacity": 0.5,
          },
        });

        mapRef.current.addLayer({
          id: "clusters-outline",
          type: "line",
          source: "clusters",
          paint: {
            "line-color": "#ff0000",
            "line-width": 2,
          },
        });

        const popupHover = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        mapRef.current.on("mousemove", "clusters-layer", (e) => {
          const props = e.features?.[0]?.properties;
          if (!props) return;

          popupHover
            .setLngLat(e.lngLat)
            .setHTML(
              `
              <strong>${props.name}</strong><br/>
              Cluster ID: ${props.cluster_id}<br/>
              Accidents: ${props.accidents}<br/>
              Indications: ${props.indications}<br/>
              Interventions: ${props.interventions}<br/>
              Last Updated: ${props.latestUpdate}
            `
            )
            .addTo(mapRef.current);
        });

        mapRef.current.on("mouseleave", "clusters-layer", () => {
          mapRef.current.getCanvas().style.cursor = "";
          popupHover.remove();
        });

        // ✅ NO click handler here (READ ONLY)
      });
    }

    return () => {
      if (mapRef.current) mapRef.current.remove();
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
  }, [darkMode]);

  // region change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    popupRef.current.remove();
    map.flyTo({ center, zoom, essential: true });
  }, [center, zoom]);

  // show selected point always (your fixed logic)
  useEffect(() => {
    const map = mapRef.current;
    if (!selectedPoint || !map) return;

    const showPoint = () => {
      const [lng, lat] = selectedPoint;

      popupRef.current
        .setLngLat([lng, lat])
        .setHTML(
          `<h3>Θέση Παρέμβασης</h3>
           <p>Longitude: ${Number(lng).toFixed(6)}<br/>
           Latitude: ${Number(lat).toFixed(6)}</p>`
        )
        .addTo(map);

      map.flyTo({
        center: [lng, lat],
        essential: true,
        zoom: 12,
      });
    };

    if (!map.isStyleLoaded()) {
      map.once("load", showPoint);
      return;
    }

    showPoint();
  }, [selectedPoint]);

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: "100%",
      }}
    />
  );
};

export default MapSelectReadOnly;
